// Shared between the Worker and the browser. Mirrors scripts/simulate.py exactly.
import QUESTIONS_RAW from './questions.json' with { type: 'json' };
import { MIN_COMMON } from './config.js';

function matrixFor(q) {
  const n = q.options.length;
  if (q.kind === 'custom') return q.matrix;
  return Array.from({ length: n }, (_, a) =>
    Array.from({ length: n }, (_, b) => {
      if (q.kind === 'same') return a === b ? 1 : 0;
      if (q.kind === 'complement') return a !== b ? 1 : 0;
      return 1 - Math.abs(a - b) / (n - 1); // ordinal
    }),
  );
}

export const QUESTIONS = QUESTIONS_RAW.questions
  .filter((q) => q.active !== false)
  .map((q) => ({ ...q, M: matrixFor(q) }));
export const QBYID = Object.fromEntries(QUESTIONS.map((q) => [q.id, q]));

/** answers: { [qid]: { choice: number } } — weighted mean of per-question agreement. */
export function agreement(me, them) {
  let num = 0, den = 0, n = 0;
  for (const q of QUESTIONS) {
    const a = me[q.id], b = them[q.id];
    if (!a || !b) continue;
    const w = q.weight ?? 1;
    num += w * q.M[a.choice][b.choice];
    den += w;
    n++;
  }
  return [den ? num / den : null, n];
}

export function compatibility(a, b) {
  const [score, n] = agreement(a, b);
  if (n < MIN_COMMON) return null;
  // Confidence penalty: two people who overlap on only a handful of questions
  // can't reach a headline number. Straight from OKCupid's old formula.
  return Math.max(0, score - 1 / n);
}

export function explain(me, them) {
  const agree = [], clash = [];
  for (const q of QUESTIONS) {
    const a = me[q.id], b = them[q.id];
    if (!a || !b) continue;
    const m = q.M[a.choice][b.choice];
    if (m >= 0.99) agree.push({ q, a, b });
    else if (m <= 0.01) clash.push({ q, a, b });
  }
  // Weight first (a triple-weighted agreement is the headline), then rarity: a shared
  // 1-in-4 answer beats a shared coin flip.
  const rank = (e) => [(e.q.weight ?? 1), e.q.options.length];
  const cmp = (x, y) => (rank(y)[0] - rank(x)[0]) || (rank(y)[1] - rank(x)[1]);
  agree.sort(cmp);
  clash.sort(cmp);
  return { agree, clash };
}

/** Validate and normalise an answers object from an untrusted source (a PDS record). */
export function sanitizeAnswers(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  for (const q of QUESTIONS) {
    const a = raw[q.id];
    if (!a || typeof a !== 'object') continue;
    const choice = Number(a.choice);
    if (!Number.isInteger(choice) || choice < 0 || choice >= q.options.length) continue;
    out[q.id] = { choice };
  }
  return Object.keys(out).length ? out : null;
}
